import "./Rooms.scss";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import MetaDecorator from "../../MetaDecorator";
import useRooms from "../../hooks/useRooms";

// Olive's rooms widget is configured to target "#root > .rooms". On mount it
// empties that node and inserts its own #olive-root in front of it. Anything
// React still owns inside it is therefore deleted behind React's back, and the
// next reconcile throws "NotFoundError: Failed to execute 'removeChild'" -
// which tears down the whole React tree, leaving the page rendered but with
// every event handler dead until a reload. So the node Olive targets stays
// permanently empty, and our own listing lives in a sibling React fully owns.
const OLIVE_ROOT_ID = "olive-root";

// How long to wait for Olive before falling back to our own listing. Olive
// mounts ~1.2s after a route change and retries twice at 1s, so this sits
// past its last attempt: long enough not to flash our list over a healthy
// Olive load, short enough to still show rooms if Olive never arrives.
const OLIVE_FALLBACK_DELAY_MS = 4000;

const useOliveMounted = () => {
  const [mounted, setMounted] = useState(
    () => !!document.getElementById(OLIVE_ROOT_ID)
  );
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    const check = () => {
      if (document.getElementById(OLIVE_ROOT_ID)) setMounted(true);
    };
    check();

    // Olive mounts asynchronously, and remounts on every route change, so keep
    // watching for the whole life of the page rather than checking once.
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    const timer = window.setTimeout(() => setWaited(true), OLIVE_FALLBACK_DELAY_MS);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, []);

  return { oliveMounted: mounted, waitedForOlive: waited };
};

const useMousePosition = (enabled: boolean) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Only track while our own listing is on screen: this sets state on every
    // mousemove, and re-rendering behind Olive's widget is pure jank.
    if (!enabled) return;

    const updateMousePosition = (event: MouseEvent) => {
      setMousePosition({ x: event.pageX, y: event.pageY });
    };
    window.addEventListener("mousemove", updateMousePosition);

    return () => window.removeEventListener("mousemove", updateMousePosition);
  }, [enabled]);

  return mousePosition;
};

const Rooms = () => {
  const rooms = useRooms().rooms;
  const { oliveMounted, waitedForOlive } = useOliveMounted();

  const showOwnListing = waitedForOlive && !oliveMounted && !!rooms;
  const { x, y } = useMousePosition(showOwnListing);

  // const roomsSorted = [rooms[0], rooms[1], rooms[3], rooms[2]];

  return (
    <>
      <MetaDecorator title="Annex Rooms" description="Sample description" />
      {/* Olive's insertion target - must stay empty, see note above. It is also
          the first "#root > .rooms" in the document, which is the one Olive's
          querySelector picks up. */}
      <div className="rooms" />
      {showOwnListing && rooms && (
        <div className="rooms">
          <h1 className="rooms__title"> Our Rooms</h1>
          {rooms.map((room, index) => (
            <Link
              key={index}
              to={`/rooms/${room.id}`}
              className={`rooms__link rooms__link--${index + 1}`}
            >
              {room.name}
            </Link>
          ))}
          <div
            className="rooms__images-container tablet-and-above"
            style={{ transform: `translate(${x}px, ${y}px)` }}
          >
            {rooms.map((room, index) => (
              <img
                key={index}
                src={room.mainImageUrl}
                alt={room.name}
                className={`rooms__image rooms__image--${index + 1}`}
                loading="lazy"
              />
            ))}
          </div>
          <div className="rooms__images-container mobile-below-tablet">
            {rooms.map((room, index) => (
              <img
                key={index}
                src={room.mainImageUrl}
                alt={room.name}
                className={`rooms__image rooms__image--${index + 1}`}
                loading="lazy"
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Rooms;
